/**
 * Range-aware concentrated-liquidity APR math.
 *
 * Models the expected fee return of a concentrated LP position — whether
 * prospective (create-position flow) or live (manage-position) — using the
 * canonical fees-share approach: the position's share of in-range virtual
 * liquidity determines its share of the pool's swap fees.
 *
 * Pure function family. No MobX, no React, no thick-v1 imports. Every caller
 * brings its own inputs and handles formatting.
 *
 * Derivation:
 *
 *   If the current tick is outside [tickLower, tickUpper) the position
 *   earns zero fees until price re-enters. Otherwise
 *
 *     feesShare   = positionL / combinedL
 *     dailyFees$  = volumeUsd24h * feeRate * feesShare
 *     aprPct      = (dailyFees$ * 365 / positionValueUsd) * 100
 *
 *   where `combinedL` depends on whether the position is already part of
 *   the pool's liquidity book: for a prospective new position we sum
 *   position + pool, for an existing live position the pool figure
 *   already includes it.
 *
 *   `feeRate` is `feeTierBps / 1_000_000` because Uniswap expresses fee
 *   tiers in hundredths of a bip — 3000 == 0.3%.
 *
 * Capital efficiency uses the Uniswap in-range value identity
 *   V = L * (2*sqrtP - sqrtPa - sqrtP^2 / sqrtPb)
 * which collapses to `V = L * 2 * sqrtP` for the full range `[0, ∞]`. The
 * ratio is therefore independent of deposit size — it is a pure property
 * of the range geometry at the current price. We handle the three tick
 * regimes (below / in-range / above) separately so the multiplier stays
 * continuous at tickLower and tickUpper.
 */

export interface ConcentratedAprInputs {
  /** Pool state: current on-chain tick snapshot. */
  currentTick: number
  /** Pool fee tier in hundredths of a bip (500, 3000, 10000, 1337). */
  feeTierBps: number
  /** Trailing 24h pool-wide volume in USD. */
  volumeUsd24h: number
  /**
   * Pool's in-range virtual liquidity at the current tick, summed across
   * every LP whose range straddles currentTick. Comes from the
   * tick-distribution store's `activeLiquidityAt(currentTick)`.
   */
  totalActiveLiquidity: bigint

  /** Position's lower tick (inclusive). */
  tickLower: number
  /** Position's upper tick (exclusive). */
  tickUpper: number
  /** Position's virtual liquidity L — from Position.fromAmountsX. */
  positionLiquidity: bigint
  /** Position's current USD value (sum of token0+token1 notional). */
  positionValueUsd: number

  /**
   * True when `totalActiveLiquidity` already includes `positionLiquidity`
   * — i.e. the position is live on-chain (manage-position flow). Defaults
   * to false for the create-position flow, where the prospective L is
   * modelled as additive to the book.
   */
  positionAlreadyInPool?: boolean
}

export interface ConcentratedAprResult {
  /** Annualised percentage return, e.g. `47.3`. Zero when out of range. */
  aprPct: number
  /** Whether the current tick sits within [tickLower, tickUpper). */
  inRange: boolean
  /**
   * Fraction of the in-range fee stream the position captures, in [0, 1].
   * Zero when out of range or when combined liquidity is zero.
   */
  feesShare: number
  /** Expected USD fees per day at current pool activity. */
  dailyFeesUsd: number
  /**
   * Ratio of this position's virtual liquidity to a full-range position
   * holding the same USD deposit at current price. `1` for full-range,
   * grows without bound as the range narrows. Zero for degenerate ranges.
   */
  capitalEfficiency: number
}

const PRECISION_SCALE = 1_000_000_000_000n

/**
 * Compute the range-aware APR result for a prospective or live
 * concentrated-liquidity position. See module docstring for the math.
 */
export function concentratedApr(args: ConcentratedAprInputs): ConcentratedAprResult {
  const capitalEfficiency = computeCapitalEfficiency({
    currentTick: args.currentTick,
    tickLower: args.tickLower,
    tickUpper: args.tickUpper,
  })

  const inRange =
    args.tickLower <= args.currentTick && args.currentTick < args.tickUpper

  if (!inRange) {
    return {
      aprPct: 0,
      inRange: false,
      feesShare: 0,
      dailyFeesUsd: 0,
      capitalEfficiency,
    }
  }

  if (args.positionLiquidity <= 0n) {
    return {
      aprPct: 0,
      inRange: true,
      feesShare: 0,
      dailyFeesUsd: 0,
      capitalEfficiency,
    }
  }

  const combined = args.positionAlreadyInPool
    ? args.totalActiveLiquidity
    : args.totalActiveLiquidity + args.positionLiquidity

  if (combined <= 0n) {
    return {
      aprPct: 0,
      inRange: true,
      feesShare: 0,
      dailyFeesUsd: 0,
      capitalEfficiency,
    }
  }

  const feesShare =
    Number((args.positionLiquidity * PRECISION_SCALE) / combined) /
    Number(PRECISION_SCALE)

  const feeRate = args.feeTierBps / 1_000_000
  const dailyFeesUsd =
    Number.isFinite(args.volumeUsd24h) && args.volumeUsd24h > 0
      ? args.volumeUsd24h * feeRate * feesShare
      : 0

  if (
    !args.positionValueUsd ||
    args.positionValueUsd <= 0 ||
    !Number.isFinite(args.positionValueUsd)
  ) {
    return {
      aprPct: 0,
      inRange: true,
      feesShare,
      dailyFeesUsd,
      capitalEfficiency,
    }
  }

  const aprPct = (dailyFeesUsd * 365) / args.positionValueUsd * 100

  return {
    aprPct: Number.isFinite(aprPct) ? aprPct : 0,
    inRange: true,
    feesShare,
    dailyFeesUsd,
    capitalEfficiency,
  }
}

/**
 * Ratio of a concentrated position's virtual liquidity to a full-range
 * position holding the same deposit value at the current price.
 *
 * Works in Number-land because all ticks in Uniswap v3 fit comfortably
 * within IEEE-754 range (1.0001^(MAX_TICK/2) ~ 1.7e19).
 */
function computeCapitalEfficiency(args: {
  currentTick: number
  tickLower: number
  tickUpper: number
}): number {
  if (args.tickUpper <= args.tickLower) return 0

  const sqrtP = Math.pow(1.0001, args.currentTick / 2)
  const sqrtPa = Math.pow(1.0001, args.tickLower / 2)
  const sqrtPb = Math.pow(1.0001, args.tickUpper / 2)

  if (!Number.isFinite(sqrtP) || !Number.isFinite(sqrtPa) || !Number.isFinite(sqrtPb)) {
    return 0
  }
  if (sqrtP <= 0 || sqrtPa <= 0 || sqrtPb <= 0) return 0

  let multiplier: number
  if (args.currentTick < args.tickLower) {
    const denom = sqrtP * (1 / sqrtPa - 1 / sqrtPb)
    multiplier = denom > 0 ? 2 / denom : 0
  } else if (args.currentTick >= args.tickUpper) {
    const denom = sqrtPb - sqrtPa
    multiplier = denom > 0 ? (2 * sqrtP) / denom : 0
  } else {
    const denom = 2 * sqrtP - sqrtPa - (sqrtP * sqrtP) / sqrtPb
    multiplier = denom > 0 ? (2 * sqrtP) / denom : 0
  }

  return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 0
}
